import { $TSFixMe } from '@calefy-inc/utilityTypes';
import { formatListForDisplay, safelyGetAttributes } from '../../util';

const IMPLIED_RATE_TOLERANCE = 1e-5; // tolerance for the rate and implied rate to be considered different
const IMPLIED_PREMIUM_TOLERANCE = 1e-2; // tolerane for the premium and implied premium to be considered different (to the cent level is probably fine)

interface WithIdInput {
  id: string;
}
class WithId {
  id: string;

  constructor({ id }: WithIdInput) {
    this.id = id;
  }
}

interface InsurancePolicyInput extends WithIdInput {
  id: string;
  description: string;
  policyEffectiveDate: Date;
  rawPolicyEffectiveDate: string;
  policyExpiryDate: Date;
  rawPolicyExpiryDate: string;
  coverages: Array<Coverage>;
  policyNumber: string;
  additionalInformation?: object;
  commissionRate?: number;
}
export class InsurancePolicy extends WithId {
  description: string;
  policyEffectiveDate: Date;
  rawPolicyEffectiveDate: string;
  policyExpiryDate: Date;
  rawPolicyExpiryDate: string;
  coverages: Array<Coverage>;
  policyNumber: string;
  additionalInformation: object;
  commissionRate?: number;

  constructor({
    id,
    description,
    policyEffectiveDate,
    rawPolicyEffectiveDate,
    policyExpiryDate,
    rawPolicyExpiryDate,
    coverages,
    policyNumber,
    additionalInformation,
    commissionRate,
  }: InsurancePolicyInput) {
    super({ id });

    this.description = description;
    this.policyEffectiveDate = policyEffectiveDate;
    this.rawPolicyEffectiveDate = rawPolicyEffectiveDate;
    this.policyExpiryDate = policyExpiryDate;
    this.rawPolicyExpiryDate = rawPolicyExpiryDate;
    this.policyNumber = policyNumber;

    this.coverages = coverages;
    this.additionalInformation = additionalInformation
      ? additionalInformation
      : {};
    this.commissionRate = commissionRate;
  }

  getAllCoveragesOverridden() {
    return this.coverages.every((coverage) =>
      coverage.getAllPerilsOverridden(),
    );
  }

  /**
   * Get the TIV for this policy as a whole
   */
  getTiv() {
    return this.coverages.reduce((tiv, coverage) => {
      const coverageTiv = coverage.getTiv();
      return coverageTiv ? tiv + coverageTiv : tiv;
    }, 0);
  }

  /**
   * Get the total premium for all coverages
   */
  getPremium() {
    return this.coverages.reduce((premium, coverage) => {
      return premium + coverage.getPremium();
    }, 0);
  }

  static createFromBackendResponse(backend: $TSFixMe) {
    let additionalInformation = {};
    try {
      const rawAdditionalInformation = safelyGetAttributes(backend, [
        'additionalInformation',
      ]);
      if (typeof rawAdditionalInformation === 'string') {
        additionalInformation = JSON.parse(rawAdditionalInformation);
      } else {
        additionalInformation = {};
      }
    } catch (e) {
      console.error(e);
      additionalInformation = {};
    }
    return new InsurancePolicy({
      ...backend,
      rawPolicyEffectiveDate: backend.policyEffectiveDate,
      rawPolicyExpiryDate: backend.policyExpiryDate,
      // @ts-expect-error
      coverages: (backend.coverages || []).map((backendCoverage) =>
        Coverage.createFromBackendResponse(backendCoverage),
      ),
      additionalInformation,
    });
  }
}

interface CoverageInput extends WithIdInput {
  id: string;
  name: string;
  perils: Array<Peril>;
  coveredLocations: Array<CoveredLocation>;
}
export class Coverage extends WithId {
  name: string;
  perils: Array<Peril>;
  coveredLocations: Array<CoveredLocation>;

  constructor({ id, name, perils, coveredLocations }: CoverageInput) {
    super({ id });

    this.name = name;
    this.perils = perils;
    this.coveredLocations = coveredLocations;
  }

  getDisplayName() {
    if (this.name === 'Property' && this.coveredLocations.length > 0) {
      return `${formatListForDisplay(
        this.coveredLocations.map((coveredLocation) =>
          coveredLocation.getDisplayAddress(),
        ),
      )}`;
    }
    return this.name;
  }

  getAllCoveredLocationsString() {
    return formatListForDisplay(
      this.coveredLocations.map((loc) => loc.getDisplayAddress()),
    );
  }

  getTiv(): number | undefined {
    // this is fucking stupid. Make sure this is up to date with the backend VagoCommerciPackPerils.Property
    const relevantPerilNames = [
      'Building',
      'Contents',
      'Loss of Rent',
      'Miscellaneous',
    ];
    const relevantPerils = this.perils.filter((peril) =>
      relevantPerilNames.some((name) => peril.name === name),
    );
    const tiv = relevantPerils.reduce((sum, peril) => {
      return sum + peril.limit;
    }, 0);
    return tiv;
  }

  getPremium({ filter = () => true }: GetPremiumOptions = {}) {
    return this.perils.filter(filter).reduce((premium, peril) => {
      return premium + peril.premium;
    }, 0);
  }

  /**
   * Have all of the perils been overridden?
   */
  getAllPerilsOverridden() {
    return this.perils.every((peril) => peril.override);
  }

  /**
   * Static Functions
   */
  static createFromBackendResponse(backendResponse: $TSFixMe) {
    return new Coverage({
      ...backendResponse,
      // @ts-expect-error
      perils: (backendResponse.perils || []).map((backendPeril) =>
        Peril.createFromBackendResponse(backendPeril),
      ),
      coveredLocations: (backendResponse.coveredLocations || []).map(
        // @ts-expect-error
        (backendCoveredLocation) =>
          CoveredLocation.createFromBackendResponse(backendCoveredLocation),
      ),
    });
  }
}

interface GetPremiumOptions {
  filter?: (p: Peril) => boolean; // an optional filter to apply to the filters to include in the premium
}

export interface PerilInput extends WithIdInput {
  id: string;
  name: string;
  limit: number;
  deductible: number;
  premium: number | string;
  rate: number;
  override?: boolean;
  displayName?: string;
  basis?: number;
}
export class Peril extends WithId {
  name: string;
  limit: number;
  deductible: number;
  premium: number;
  rate: number;
  override: boolean;
  displayName?: string; // an optional override to the display name from the backend
  basis?: number;

  constructor({
    id,
    name,
    limit,
    deductible,
    premium,
    rate,
    override,
    displayName,
    basis,
  }: PerilInput) {
    super({ id });

    this.name = name;
    this.limit = limit;
    this.deductible = deductible;
    this.premium = Number(premium);
    this.rate = rate;
    this.override = !!override;
    this.displayName = displayName;
    this.basis = basis;
  }

  getImpliedRate() {
    return this.premium / this.getBasis();
  }

  getImpliedPremium() {
    return this.getBasis() * this.rate;
  }

  impliedRateDifferent(tolerance: number = IMPLIED_RATE_TOLERANCE) {
    return Math.abs(this.getImpliedRate() - this.rate) > tolerance;
  }

  impliedPremiumDifferent(tolerance: number = IMPLIED_PREMIUM_TOLERANCE) {
    return Math.abs(this.getImpliedPremium() - this.premium) > tolerance;
  }

  getBasis() {
    return this.basis ? this.basis : this.limit;
  }

  /**
   * Determine if another peril matches this one (done by checking the attributes)
   */
  match(other: Peril): boolean {
    const keysToCheck: Array<keyof Peril> = [
      'id',
      'name',
      'limit',
      'deductible',
      'premium',
      'rate',
      'basis',
    ];
    return keysToCheck.every((key) => {
      const match = this[key] === other[key];
      return match;
    });
  }

  copyWithAmendments(input: Partial<PerilInput>) {
    return new Peril({ ...this, ...input });
  }

  copy() {
    return this.copyWithAmendments({});
  }

  static createFromBackendResponse(backend: $TSFixMe) {
    const peril = new Peril({ ...backend });
    return peril;
  }
}

interface CoveredLocationInput extends WithIdInput {
  description: string;
  address: string;
  city: string;
  province: string;
  postal: string;
}
export class CoveredLocation extends WithId {
  description: string;
  address: string;
  city: string;
  province: string;
  postal: string;

  constructor({
    id,
    description,
    address,
    city,
    province,
    postal,
  }: CoveredLocationInput) {
    super({ id });

    this.description = description;
    this.address = address;
    this.city = city;
    this.province = province;
    this.postal = postal;
  }

  getDisplayAddress() {
    return [this.address, this.city, this.province, this.postal]
      .filter((elem) => !!elem)
      .join(', ');
  }

  /**
   * Static Functions
   */
  static createFromBackendResponse(backend: $TSFixMe) {
    return new CoveredLocation({ ...backend });
  }
}
